import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAllBols } from "@/lib/bol.functions";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const bolsSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(z.literal("created_at").optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/bols/")({
  validateSearch: zodValidator(bolsSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: BolsIndex,
});

function BolsIndex() {
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sort = urlSearch.sort ?? "desc";
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const [status, setStatus] = useState<"all" | "draft" | "finalized" | "voided">("all");
  const listFn = useServerFn(listAllBols);
  const { data, isLoading } = useQuery({
    queryKey: ["bols-all", status, fromIso, toIso, sort],
    queryFn: () => listFn({ data: {
      ...(status === "all" ? {} : { status }),
      fromIso, toIso, sort,
    } }),
  });
  const rows = data?.bols ?? [];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        title="Bills of Lading"
        description="All freight paperwork generated from the pack station and shipment workflows."
      />

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <DateRangeFilter
              value={{ start: urlSearch.start, end: urlSearch.end }}
              onChange={(r) => navigate({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
            />
            <SortDirectionToggle
              value={sort}
              onChange={(s) => navigate({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
            />
            <span className="text-xs text-muted-foreground">{rows.length} BOL{rows.length === 1 ? "" : "s"}</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>BOL #</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rev</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-muted-foreground">No BOLs found.</TableCell></TableRow>
              ) : rows.map((b: any) => {
                const shp = b.shipments;
                const cust = shp?.customers?.name ?? "—";
                return (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono">{b.bol_number ?? <span className="text-muted-foreground">(draft)</span>}</TableCell>
                    <TableCell><StatusPill tone={statusTone(b.status)} dot>{b.status}</StatusPill></TableCell>
                    <TableCell>v{b.revision ?? 1}</TableCell>
                    <TableCell>{cust}</TableCell>
                    <TableCell>{shp?.carrier ?? "—"}</TableCell>
                    <TableCell className="uppercase text-xs">{shp?.freight_mode ?? "parcel"}</TableCell>
                    <TableCell>{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild size="sm" variant="outline">
                          <Link to="/app/shipments/$shipmentId/bol" params={{ shipmentId: b.shipment_id }}>Open</Link>
                        </Button>
                        <Button asChild size="sm" variant="ghost">
                          <Link to="/app/shipments/$shipmentId/bol/print" params={{ shipmentId: b.shipment_id }} target="_blank">Print</Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}